import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

export interface Category {
  id: string;
  name: string;
  kind: 'revenue' | 'expense';
}

export interface CategoryRule {
  id: string;
  description_pattern: string;
  category_id: string;
}

export function useCategories() {
  const { tenantId } = useTenant();
  const [categories, setCategories] = useState<Category[]>([]);
  const [rules, setRules] = useState<CategoryRule[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    const [catRes, ruleRes] = await Promise.all([
      supabase
        .from('categories')
        .select('id, name, kind')
        .eq('tenant_id', tenantId)
        .order('name'),
      supabase
        .from('category_rules')
        .select('id, description_pattern, category_id')
        .eq('tenant_id', tenantId),
    ]);
    setCategories((catRes.data as unknown as Category[]) ?? []);
    setRules((ruleRes.data as unknown as CategoryRule[]) ?? []);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Cria categoria se não existir, retorna id
  async function ensureCategory(name: string, kind: 'revenue' | 'expense'): Promise<string> {
    const existing = categories.find(
      (c) => c.name.toLowerCase() === name.toLowerCase() && c.kind === kind
    );
    if (existing) return existing.id;

    const { data, error } = await supabase
      .from('categories')
      .insert({ tenant_id: tenantId, name: name.trim(), kind } as never)
      .select('id')
      .single();
    if (error) throw new Error('Erro ao criar categoria: ' + error.message);
    const newCat = data as unknown as { id: string };
    await fetchAll();
    return newCat.id;
  }

  // Salva regra descrição → categoria (upsert)
  async function saveRule(descriptionPattern: string, categoryId: string) {
    await supabase
      .from('category_rules')
      .upsert(
        { tenant_id: tenantId, description_pattern: descriptionPattern, category_id: categoryId } as never,
        { onConflict: 'tenant_id,description_pattern' }
      );
    await fetchAll();
  }

  // Aplica categoria a uma transação + opcionalmente a todas com a mesma descrição
  async function applyCategory(
    transactionId: string,
    description: string,
    categoryId: string,
    applyToAll: boolean
  ) {
    // Atualiza a transação específica
    await supabase
      .from('transactions')
      .update({ category_id: categoryId } as never)
      .eq('id', transactionId);

    if (applyToAll && tenantId) {
      // Atualiza todas com a mesma descrição
      await supabase
        .from('transactions')
        .update({ category_id: categoryId } as never)
        .eq('tenant_id', tenantId)
        .eq('description', description);

      // Salva regra para futuros imports
      await saveRule(description, categoryId);
    }
  }

  return { categories, rules, loading, ensureCategory, applyCategory, saveRule, refetch: fetchAll };
}
